import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  Alert,
  Box,
  Link,
  Typography,
} from '@mui/material';
import {Page} from '@vedmarket/ui-kit';
import {COLORS, NOTIFICATION_ACTION, ROUTES} from 'config/constants';
import {
  NOTIFICATIONS_REQUEST,
  useNotifications,
  useNotificationSections,
  useGetNotificationSectionsQuery,
  useGetNotificationsQuery,
  useReadNotificationsMutation,
  useUnreadNotificationsMutation,
} from 'api/notifications';
import {useGetUserMutation, useUser} from 'api/auth';
import NotificationsFilters from './filters';
import NotificationsTable from './table';

import './notifications.scss';

const Notifications = () => {
  const {t, i18n} = useTranslation();

  const user = useUser();
  const data = useNotifications();
  const sections = useNotificationSections();

  const [ params, setParams ] = useState<NotificationsRequest>(NOTIFICATIONS_REQUEST);
  const [ tableVersion, setTableVersion ] = useState(0);
  const [ isUpdating, setIsUpdating ] = useState(false);
  const [ isRefreshRequested, setIsRefreshRequested ] = useState(false);
  const [ actionError, setActionError ] = useState(false);

  const [ getUser ] = useGetUserMutation();
  const [ readNotifications, {isLoading: isReading} ] = useReadNotificationsMutation();
  const [ unreadNotifications, {isLoading: isMarkingUnread} ] = useUnreadNotificationsMutation();

  const {
    data: queryData,
    currentData,
    isFetching,
    isError,
    refetch,
  } = useGetNotificationsQuery(params, {refetchOnMountOrArgChange: true});
  const {isError: sectionsError} = useGetNotificationSectionsQuery();

  const unreadCount = user.meta?.unread_notifications_count || 0;
  const isSelected = Boolean(params.section);
  const hasSearch = Boolean(params.search);
  const hasCriteria = isSelected || hasSearch;
  const hasQueryError = isError || sectionsError;
  const hasError = hasQueryError || actionError;
  const isEmptyList = data?.count === 0;
  const hasNoNotifications = isEmptyList && !hasCriteria;
  const showEmptyState = hasNoNotifications && !hasQueryError;
  const hasNoResults = isEmptyList && !hasQueryError;
  const isTableUpdating = isUpdating || isFetching;
  const actionBusy = isReading || isMarkingUnread;
  const isTableBusy = isTableUpdating || actionBusy;
  const isTableLoading = isFetching && !queryData;
  const hasUnreadNotifications = unreadCount > 0;

  useEffect(() => {
    const hasNextPage = params.page > 1;
    const hasEmptyPage = currentData?.results.length === 0;
    const shouldResetPage = hasNextPage && hasEmptyPage;

    if (shouldResetPage) {
      setParams((current) => ({...current, page: 1}));
      setTableVersion((current) => current + 1);
    }
  }, [ currentData, params.page ]);

  useEffect(() => {
    if (!isRefreshRequested) return;

    refetch().then(() => {
      setIsRefreshRequested(false);
      setIsUpdating(false);
    });
  }, [ isRefreshRequested ]);

  const handleAction = async (request: NotificationsActionRequest) => {
    const hasNotifications = request.notifications.length > 0;
    const isActionBlocked = isTableBusy || !hasNotifications;

    if (isActionBlocked) return false;

    setIsUpdating(true);
    setActionError(false);

    const isUnread = request.action === NOTIFICATION_ACTION.UNREAD;
    const changeNotificationsStatus = isUnread ? unreadNotifications : readNotifications;
    const ids = request.notifications.map((notification) => notification.id);

    try {
      await changeNotificationsStatus({ids}).unwrap();
      await getUser({}).unwrap();
      setIsRefreshRequested(true);

      return true;
    } catch {
      setActionError(true);
      setIsUpdating(false);
      return false;
    }
  };

  const handleTableRequest = ({page, limit}: NotificationsTableRequest) => {
    setParams((current) => ({...current, page, limit}));
  };

  const handleFiltersApply = (section: string[]) => {
    const selectedSection = section.length ? section.join(',') : undefined;

    setParams((current) => ({...current, page: 1, section: selectedSection}));
    setTableVersion((current) => current + 1);
  };

  const handleSearch = (search: string) => {
    const searchQuery = search || undefined;

    setParams((current) => ({...current, page: 1, search: searchQuery}));
    setTableVersion((current) => current + 1);
  };

  const header = (
    <header className='notifications-page__header'>
      <Link
        href={`/${ROUTES.CABINET}`}
        underline='hover'
        sx={{cursor: 'pointer', color: COLORS.LIGHT_TEXT_SECONDARY}}
      >
        <Typography
          variant='body2'
          sx={{color: COLORS.LIGHT_TEXT_SECONDARY, lineHeight: '21px', letterSpacing: 0}}
        >
          {t('menu__text__main')}
        </Typography>
      </Link>
      <div className='notifications-page__title'>
        <Typography
          variant='h4'
          sx={{fontSize: 32, fontWeight: 700, lineHeight: '40px', letterSpacing: 0}}
        >
          {t('notifications__text__title')}
        </Typography>
        {hasUnreadNotifications && (
          <Typography
            component='span'
            variant='h6'
            sx={{color: COLORS.LIGHT_TEXT_DISABLED, fontSize: 18, lineHeight: '28px', letterSpacing: 0}}
          >
            +{unreadCount}
          </Typography>
        )}
      </div>
    </header>
  );

  const table = hasNoResults
    ? <Typography variant='body1'>{t('notifications__text__noResults')}</Typography>
    : <NotificationsTable
      key={tableVersion}
      notifications={data?.results || []}
      count={data?.count || 0}
      limit={params.limit}
      busy={isTableBusy}
      onAction={handleAction}
      onRequest={handleTableRequest}
    />;

  const hasSections = sections.length > 0;
  const filters = hasSections
    ? <NotificationsFilters options={sections} onApply={handleFiltersApply} />
    : undefined;

  return (
    <Box
      component='main'
      className='notifications-page'
      lang={i18n.language}
      sx={{'.dictionary-page__filters-card .MuiTypography-h6': {lineHeight: '28px'}}}
    >
      {hasError && (
        <Alert severity='error'>
          <Typography variant='body1'>{t('common__text__error')}</Typography>
        </Alert>
      )}
      {showEmptyState ? (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: '24px'}}>
          {header}
          <Typography
            variant='body1'
            sx={{color: COLORS.BLACK_DARK, lineHeight: '24px', letterSpacing: 0}}
          >
            {t('notifications__text__empty')}
          </Typography>
        </Box>
      ) : <Page
        isSelected={isSelected}
        header={header}
        onSearch={handleSearch}
        isLoading={isTableLoading}
        table={table}
        filters={filters}
      />}
    </Box>
  );
};

export default Notifications;
